import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HSGLogo from '@/components/feature/HSGLogo';
import useSEO from '@/hooks/useSEO';

// ─── Data ────────────────────────────────────────────────────────────────────

const tools = [
  {
    id: 'logo-export',
    path: '/logo-export',
    icon: 'ri-download-cloud-line',
    label: 'Logo Export',
    tagline: 'Download & embed',
    description:
      'Export the HSG logo in every format you need — SVG, PNG, favicon, and embed code. Covers all four approved color variants.',
    features: ['SVG & PNG downloads', 'Favicon generator', 'Embed code snippets', '4 color variants'],
    accent: '#C9A96E',
    bg: 'bg-[#1A1A1A]',
    textPrimary: 'text-white',
    textSecondary: 'text-white/50',
    badge: 'Most used',
    badgeBg: 'bg-[#C9A96E]/20 text-[#C9A96E] border border-[#C9A96E]/30',
  },
  {
    id: 'business-card',
    path: '/business-card',
    icon: 'ri-contacts-book-2-line',
    label: 'Business Card',
    tagline: 'Print-ready cards',
    description:
      'Design and print business cards for the whole team. Mix designs per person, set copy counts, and export a print-ready PDF sheet.',
    features: ['4 design variants', 'Team roster system', 'Duplicate per person', 'Print-ready PDF'],
    accent: '#C9A96E',
    bg: 'bg-[#2C2C2C]',
    textPrimary: 'text-white',
    textSecondary: 'text-white/50',
    badge: null,
    badgeBg: '',
  },
  {
    id: 'letterhead',
    path: '/letterhead',
    icon: 'ri-file-text-line',
    label: 'Letterhead',
    tagline: 'Formal correspondence',
    description:
      'Create branded A4 letterhead for formal correspondence. Edit all fields live, choose from 4 layout variants, and print or save as PDF.',
    features: ['4 layout variants', 'Live A4 preview', 'Editable all fields', 'Print / PDF export'],
    accent: '#C9A96E',
    bg: 'bg-[#F7F4EF]',
    textPrimary: 'text-[#1A1A1A]',
    textSecondary: 'text-[#6B6B6B]',
    badge: null,
    badgeBg: '',
  },
  {
    id: 'brand',
    path: '/brand',
    icon: 'ri-palette-line',
    label: 'Brand Guidelines',
    tagline: 'The full rulebook',
    description:
      'The complete HSG brand reference — color palette with hex codes, typography scale, spacing system, logo usage rules, and social sharing specs.',
    features: ['Color palette & codes', 'Type scale', 'Spacing system', 'Logo do\'s & don\'ts'],
    accent: '#8B7355',
    bg: 'bg-white',
    textPrimary: 'text-[#1A1A1A]',
    textSecondary: 'text-[#6B6B6B]',
    badge: 'Reference',
    badgeBg: 'bg-[#8B7355]/10 text-[#8B7355] border border-[#8B7355]/20',
  },
];

const quickLinks = [
  { label: 'Gold hex code', value: '#C9A96E', icon: 'ri-palette-line', copy: true },
  { label: 'Charcoal hex code', value: '#2C2C2C', icon: 'ri-palette-line', copy: true },
  { label: 'Display font', value: 'Cormorant Garamond', icon: 'ri-text', copy: true },
  { label: 'Body font', value: 'Inter', icon: 'ri-text', copy: true },
  { label: 'Logo clear space', value: 'Cap-height of "H"', icon: 'ri-ruler-line', copy: false },
  { label: 'Card size', value: '3.5" × 2"', icon: 'ri-contacts-line', copy: false },
  { label: 'Letterhead size', value: 'A4 (210 × 297mm)', icon: 'ri-file-line', copy: false },
  { label: 'OG image size', value: '1200 × 630 px', icon: 'ri-image-line', copy: false },
];

const recentUpdates = [
  { date: 'Apr 2026', label: 'Letterhead template added', icon: 'ri-file-text-line' },
  { date: 'Apr 2026', label: 'Business card team roster system', icon: 'ri-contacts-book-2-line' },
  { date: 'Mar 2026', label: 'Logo export tool launched', icon: 'ri-download-cloud-line' },
  { date: 'Mar 2026', label: 'Brand guidelines published', icon: 'ri-palette-line' },
];

// ─── Sub-components ───────────────────────────────────────────────────────────

function CopyChip({ value }: { value: string }) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(value);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = value;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <button
      onClick={handleCopy}
      className="flex items-center gap-1 text-[10px] px-2 py-1 rounded bg-[#F7F4EF] text-[#9A9A9A] hover:text-[#2C2C2C] hover:bg-[#E8E6E3] transition-all cursor-pointer whitespace-nowrap"
    >
      <i className={`text-xs ${copied ? 'ri-check-line text-[#4A7C59]' : 'ri-file-copy-line'}`}></i>
      {copied ? 'Copied' : 'Copy'}
    </button>
  );
}

// ─── Page ─────────────────────────────────────────────────────────────────────

export default function BrandAssetsPage() {
  useSEO({
    title: 'Brand Assets Hub | Higher Standard Global',
    description: 'All HSG brand tools in one place — logo export, business card, letterhead, and brand guidelines.',
    canonical: '/brand-assets',
    noIndex: true,
  });

  const navigate = useNavigate();
  const [hoveredTool, setHoveredTool] = useState<string | null>(null);

  return (
    <div className="min-h-screen bg-[#F7F4EF]">

      {/* ── Hero ── */}
      <div className="w-full bg-[#1A1A1A] relative overflow-hidden">
        {/* Subtle grid pattern */}
        <div
          className="absolute inset-0 opacity-[0.04]"
          style={{
            backgroundImage: 'linear-gradient(#C9A96E 1px, transparent 1px), linear-gradient(90deg, #C9A96E 1px, transparent 1px)',
            backgroundSize: '48px 48px',
          }}
        />
        {/* Gold accent bar */}
        <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#C9A96E]/60 to-transparent" />

        <div className="relative max-w-6xl mx-auto px-6 md:px-16 pt-20 pb-16">
          {/* Back link */}
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-1.5 text-xs text-white/40 hover:text-white/70 transition-colors cursor-pointer mb-10"
          >
            <i className="ri-arrow-left-line"></i>
            Back to site
          </button>

          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-8">
            <div>
              <p className="text-xs uppercase tracking-[0.35em] text-[#C9A96E] mb-4 font-medium">
                Higher Standard Global
              </p>
              <h1 className="font-['Cormorant_Garamond'] text-5xl md:text-6xl font-light text-white mb-4 leading-tight">
                Brand Assets
              </h1>
              <p className="text-sm text-white/50 max-w-md leading-relaxed">
                Everything you need to represent HSG consistently — logo files, print templates, and the complete brand reference, all in one place.
              </p>
            </div>

            {/* Logo showcase */}
            <div className="flex-shrink-0 border border-white/10 rounded-xl p-6 bg-white/5 backdrop-blur-sm">
              <HSGLogo variant="light" size="lg" />
              <p className="text-[10px] text-white/30 uppercase tracking-widest mt-4">Official Lockup</p>
            </div>
          </div>

          {/* Stats row */}
          <div className="flex flex-wrap items-center gap-6 mt-10 pt-10 border-t border-white/10">
            {[
              { value: '4', label: 'Brand tools' },
              { value: '4', label: 'Logo variants' },
              { value: '4', label: 'Card designs' },
              { value: '4', label: 'Letterhead layouts' },
            ].map((stat) => (
              <div key={stat.label} className="flex items-baseline gap-2">
                <span className="font-['Cormorant_Garamond'] text-3xl font-light text-[#C9A96E]">{stat.value}</span>
                <span className="text-xs text-white/40">{stat.label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── Main content ── */}
      <div className="max-w-6xl mx-auto px-6 md:px-16 py-16">

        {/* ── Tool cards ── */}
        <div className="mb-4">
          <p className="text-xs uppercase tracking-[0.3em] text-[#8B7355] mb-1 font-medium">Tools</p>
          <h2 className="font-['Cormorant_Garamond'] text-3xl font-light text-[#1A1A1A]">Brand Tools</h2>
        </div>
        <p className="text-sm text-[#6B6B6B] mb-10 max-w-lg leading-relaxed">
          Click any tool to open it. Each one is self-contained with live previews and export options.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-16">
          {tools.map((tool) => (
            <button
              key={tool.id}
              onClick={() => navigate(tool.path)}
              onMouseEnter={() => setHoveredTool(tool.id)}
              onMouseLeave={() => setHoveredTool(null)}
              className={`group relative text-left rounded-xl overflow-hidden transition-all duration-300 cursor-pointer ${tool.bg} ${
                hoveredTool === tool.id ? 'scale-[1.01]' : 'scale-100'
              }`}
            >
              {/* Card content */}
              <div className="p-7">
                {/* Top row */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className="w-11 h-11 flex items-center justify-center rounded-lg"
                    style={{ backgroundColor: `${tool.accent}20`, border: `1px solid ${tool.accent}30` }}
                  >
                    <i className={`${tool.icon} text-lg`} style={{ color: tool.accent }}></i>
                  </div>
                  <div className="flex items-center gap-2">
                    {tool.badge && (
                      <span className={`text-[9px] uppercase tracking-widest px-2 py-1 rounded-full font-medium ${tool.badgeBg}`}>
                        {tool.badge}
                      </span>
                    )}
                    <div
                      className={`w-7 h-7 flex items-center justify-center rounded-full transition-all duration-300 ${
                        hoveredTool === tool.id ? 'translate-x-0.5 -translate-y-0.5' : ''
                      }`}
                      style={{ backgroundColor: `${tool.accent}20` }}
                    >
                      <i className="ri-arrow-right-up-line text-sm" style={{ color: tool.accent }}></i>
                    </div>
                  </div>
                </div>

                {/* Label */}
                <p className={`text-[10px] uppercase tracking-[0.25em] mb-1 font-medium`} style={{ color: tool.accent }}>
                  {tool.tagline}
                </p>
                <h3 className={`font-['Cormorant_Garamond'] text-2xl font-light mb-3 ${tool.textPrimary}`}>
                  {tool.label}
                </h3>
                <p className={`text-xs leading-relaxed mb-5 ${tool.textSecondary}`}>
                  {tool.description}
                </p>

                {/* Feature pills */}
                <div className="flex flex-wrap gap-2">
                  {tool.features.map((f) => (
                    <span
                      key={f}
                      className="text-[10px] px-2.5 py-1 rounded-full font-medium"
                      style={{
                        backgroundColor: `${tool.accent}15`,
                        color: tool.accent,
                        border: `1px solid ${tool.accent}25`,
                      }}
                    >
                      {f}
                    </span>
                  ))}
                </div>
              </div>

              {/* Bottom bar */}
              <div
                className="px-7 py-3 flex items-center justify-between border-t"
                style={{ borderColor: `${tool.accent}20`, backgroundColor: `${tool.accent}08` }}
              >
                <span className={`text-xs font-medium ${tool.textSecondary}`}>Open tool</span>
                <i className="ri-arrow-right-line text-sm" style={{ color: tool.accent }}></i>
              </div>
            </button>
          ))}
        </div>

        {/* ── Two-column lower section ── */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">

          {/* Quick reference — 2 cols */}
          <div className="lg:col-span-2">
            <p className="text-xs uppercase tracking-[0.3em] text-[#8B7355] mb-1 font-medium">Quick Reference</p>
            <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1A1A] mb-6">Key Values at a Glance</h2>

            <div className="bg-white rounded-xl border border-[#E8E6E3] overflow-hidden">
              {quickLinks.map((item, i) => (
                <div
                  key={item.label}
                  className={`flex items-center justify-between px-5 py-3.5 ${
                    i < quickLinks.length - 1 ? 'border-b border-[#F7F4EF]' : ''
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-[#F7F4EF]">
                      <i className={`${item.icon} text-xs text-[#8B7355]`}></i>
                    </div>
                    <p className="text-xs text-[#6B6B6B]">{item.label}</p>
                  </div>
                  <div className="flex items-center gap-2">
                    {/* Color swatch for hex codes */}
                    {item.value.startsWith('#') && (
                      <div
                        className="w-4 h-4 rounded-full border border-[#E8E6E3] flex-shrink-0"
                        style={{ backgroundColor: item.value }}
                      />
                    )}
                    <code className="text-[11px] text-[#8B7355] bg-[#F7F4EF] px-2 py-0.5 rounded font-mono whitespace-nowrap">
                      {item.value}
                    </code>
                    {item.copy && <CopyChip value={item.value} />}
                  </div>
                </div>
              ))}
            </div>

            {/* Color swatches mini strip */}
            <div className="mt-5 bg-white rounded-xl border border-[#E8E6E3] p-5">
              <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A] mb-4">Core Palette</p>
              <div className="flex gap-3 flex-wrap">
                {[
                  { hex: '#1A1A1A', name: 'Ink' },
                  { hex: '#2C2C2C', name: 'Charcoal' },
                  { hex: '#C9A96E', name: 'Gold' },
                  { hex: '#8B7355', name: 'Warm Brown' },
                  { hex: '#F7F4EF', name: 'Cream', border: true },
                  { hex: '#E8E6E3', name: 'Stone', border: true },
                  { hex: '#6B6B6B', name: 'Body' },
                  { hex: '#FFFFFF', name: 'White', border: true },
                ].map((c) => (
                  <div key={c.hex} className="flex flex-col items-center gap-1.5">
                    <div
                      className="w-10 h-10 rounded-lg"
                      style={{
                        backgroundColor: c.hex,
                        border: c.border ? '1px solid #E8E6E3' : 'none',
                      }}
                    />
                    <p className="text-[9px] text-[#9A9A9A] text-center leading-tight">{c.name}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right column */}
          <div className="flex flex-col gap-5">

            {/* Recent updates */}
            <div>
              <p className="text-xs uppercase tracking-[0.3em] text-[#8B7355] mb-1 font-medium">Changelog</p>
              <h2 className="font-['Cormorant_Garamond'] text-2xl font-light text-[#1A1A1A] mb-6">Recent Updates</h2>
              <div className="bg-white rounded-xl border border-[#E8E6E3] overflow-hidden">
                {recentUpdates.map((item, i) => (
                  <div
                    key={item.label}
                    className={`flex items-center gap-3 px-5 py-3.5 ${
                      i < recentUpdates.length - 1 ? 'border-b border-[#F7F4EF]' : ''
                    }`}
                  >
                    <div className="w-7 h-7 flex items-center justify-center rounded-md bg-[#C9A96E]/10 flex-shrink-0">
                      <i className={`${item.icon} text-xs text-[#C9A96E]`}></i>
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-xs text-[#2C2C2C] leading-snug">{item.label}</p>
                      <p className="text-[10px] text-[#9A9A9A] mt-0.5">{item.date}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Print tips card */}
            <div className="bg-[#1A1A1A] rounded-xl p-5 flex-1">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-[#C9A96E]/20 mb-4">
                <i className="ri-printer-line text-sm text-[#C9A96E]"></i>
              </div>
              <p className="text-xs uppercase tracking-[0.25em] text-[#C9A96E] mb-2 font-medium">Print Tips</p>
              <h3 className="font-['Cormorant_Garamond'] text-xl font-light text-white mb-3">
                Getting the best results
              </h3>
              <ul className="space-y-2.5">
                {[
                  'Always enable "Background graphics" in print settings',
                  'Use 100–120gsm bond paper for letterhead',
                  'Business cards: 350gsm with matte laminate',
                  'Export as PDF, then send to printer',
                ].map((tip) => (
                  <li key={tip} className="flex items-start gap-2 text-xs text-white/50 leading-relaxed">
                    <i className="ri-checkbox-circle-line text-[#C9A96E] mt-0.5 flex-shrink-0 text-xs"></i>
                    {tip}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>

        {/* ── Typography preview strip ── */}
        <div className="mt-10 bg-white rounded-xl border border-[#E8E6E3] overflow-hidden">
          <div className="px-6 py-4 border-b border-[#E8E6E3] flex items-center justify-between">
            <p className="text-xs uppercase tracking-[0.25em] text-[#9A9A9A]">Typography Preview</p>
            <button
              onClick={() => navigate('/brand')}
              className="flex items-center gap-1.5 text-xs text-[#8B7355] hover:text-[#2C2C2C] transition-colors cursor-pointer"
            >
              Full type scale
              <i className="ri-arrow-right-line text-xs"></i>
            </button>
          </div>
          <div className="px-6 py-6 flex flex-col gap-4">
            <div className="flex items-baseline gap-4 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-[#9A9A9A] w-16 flex-shrink-0">Display</span>
              <span className="font-['Cormorant_Garamond'] text-4xl font-light text-[#1A1A1A] tracking-wide">Higher Standard Global</span>
            </div>
            <div className="flex items-baseline gap-4 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-[#9A9A9A] w-16 flex-shrink-0">Label</span>
              <span className="font-['Inter'] text-xs font-medium tracking-[0.3em] text-[#8B7355] uppercase">By Reference Only · Est. 2018</span>
            </div>
            <div className="flex items-baseline gap-4 flex-wrap">
              <span className="text-[10px] uppercase tracking-widest text-[#9A9A9A] w-16 flex-shrink-0">Body</span>
              <span className="font-['Inter'] text-sm font-light text-[#6B6B6B] leading-relaxed max-w-lg">
                We manage properties with discretion and precision for a select group of owners who value privacy, quality, and long-term relationships above all else.
              </span>
            </div>
          </div>
        </div>

        {/* ── Footer nav ── */}
        <div className="mt-10 pt-10 border-t border-[#E8E6E3] flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <HSGLogo variant="dark" size="sm" />
          </div>
          <div className="flex flex-wrap items-center gap-4">
            {[
              { label: 'Home', path: '/' },
              { label: 'About', path: '/about' },
              { label: 'Services', path: '/services' },
              { label: 'Contact', path: '/contact' },
            ].map((link) => (
              <button
                key={link.path}
                onClick={() => navigate(link.path)}
                className="text-xs text-[#9A9A9A] hover:text-[#2C2C2C] transition-colors cursor-pointer"
              >
                {link.label}
              </button>
            ))}
          </div>
          <p className="text-[10px] text-[#9A9A9A]">© 2026 Higher Standard Global</p>
        </div>

      </div>
    </div>
  );
}
